import { Badge } from "@volca/ui/components/ui/badge";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { DetailSidebarFallback } from "@/accounts-payable/common";
import { useGetVendor, useUpdateVendor } from "@/accounts-payable/query";
import type {
  ApVendorUpdate,
  ApVendorListEdit,
} from "@/accounts-payable/types";
import { useGetQuickbooksConnection } from "@/business/quickbooks-query";
import { AP_PALETTE } from "@/core/colors";
import { IncomingEmailSection } from "./incoming-email-section";
import { ProductLookupsSection } from "./product-lookups-section";
import { QuickbooksSection } from "./quickbooks-section";
import { ServiceTitanSection } from "./service-titan-section";

/**
 * Vendor management rendered inside the shared SimpleSidebar. Looks the vendor
 * up by id and persists each change inline.
 */
export const ApVendorDetail = ({
  vendorId,
  businessId,
  onClose,
}: {
  vendorId: string;
  businessId: string | null;
  onClose: () => void;
}) => {
  const { data: vendor, isPending } = useGetVendor(businessId, vendorId);
  const update = useUpdateVendor();
  const { data: connection } = useGetQuickbooksConnection(businessId);

  if (!vendor) {
    return (
      <DetailSidebarFallback
        title="Vendor"
        loading={isPending}
        errorMessage="Couldn’t load the vendor. Please try again."
        onClose={onClose}
      />
    );
  }

  const patch = (changes: ApVendorUpdate, onSuccess?: () => void) =>
    update.mutate({ businessId, vendorId: vendor.id, changes }, { onSuccess });
  const editList = (listEdit: ApVendorListEdit) =>
    update.mutate({ businessId, vendorId: vendor.id, listEdit });

  return (
    <Stack align="start" gap={0} className="h-full w-full">
      {/* Like the table, only the exceptions get a badge, beside the close
          button. */}
      <SimpleSidebarHeader
        title={vendor.name}
        actions={
          (!vendor.st_vendor_id || !vendor.active) && (
            <Stack direction="row" gap={2} align="center" className="flex-none">
              {!vendor.st_vendor_id && (
                <Badge variant="secondary" color="warning" size="sm">
                  Not linked
                </Badge>
              )}
              {!vendor.active && (
                <Badge
                  variant="secondary"
                  color={AP_PALETTE.neutral.tone}
                  size="sm"
                >
                  Inactive
                </Badge>
              )}
            </Stack>
          )
        }
        onClose={onClose}
      />

      {/* Body: sections by what the admin is doing. Space and the heading
          group them. No cards, and no rules, inside the panel. */}
      <div className="min-h-0 w-full flex-1 overflow-y-auto">
        <Stack
          align="start"
          key={vendor.id}
          gap={8}
          className="w-full px-5 py-6"
        >
          <ServiceTitanSection vendor={vendor} />
          {/* Same rule as the table: only a never-connected business hides it. */}
          {connection && connection.status !== "not_connected" && (
            <QuickbooksSection
              vendor={vendor}
              businessId={businessId}
              defaultAccount={connection.default_expense_account}
              isSaving={update.isPending}
              onUpdate={patch}
            />
          )}

          <IncomingEmailSection
            vendor={vendor}
            onUpdate={patch}
            onEditList={editList}
          />
          <ProductLookupsSection
            domains={vendor.allowed_domains ?? []}
            onEditList={editList}
          />
        </Stack>
      </div>
    </Stack>
  );
};
